/**
 * /questionsessionreport/by-session/:sessionId → redirect to the run's detail page.
 *
 * The report detail page is keyed by OperationRun id, but everything that links
 * to a report holds the SESSION id (feedback threads raised from a report carry
 * `objectId = sessionId`). Resolving here keeps the link a plain href — callers
 * don't need an async lookup before navigating, and the URL is shareable.
 */

import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';

export function QuestionSessionReportBySession() {
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['report-run-by-session', sessionId],
        queryFn: () => adminApi.getQuestionSessionReportRuns({ sessionId: sessionId! }),
        enabled: !!sessionId,
    });

    // Most-recent first from the API, so [0] is the run to show when a session
    // was reported more than once.
    const runId = data?.items?.[0]?.id;

    useEffect(() => {
        if (runId) navigate(`/questionsessionreport/${runId}`, { replace: true });
    }, [runId, navigate]);

    if (isLoading) return <p className="muted" style={{ padding: 'var(--spacing-lg)' }}>Finding the report…</p>;

    if (isError) {
        return (
            <div style={{ padding: 'var(--spacing-lg)' }}>
                <h2>Couldn’t load the report</h2>
                <p className="muted">{(error as Error).message}</p>
            </div>
        );
    }

    if (!runId) {
        return (
            <div style={{ padding: 'var(--spacing-lg)' }}>
                <h2>No report for this session</h2>
                <p className="muted">
                    Session <code>{sessionId}</code> has no generated report — it may have failed or
                    never been requested.
                </p>
                <Link to="/submissions">Back to Operations</Link>
            </div>
        );
    }

    return null;   // redirecting
}

export default QuestionSessionReportBySession;
